/**
 * Error handling utilities for consistent error management
 */

import { config } from '../services/config';
import logger from './loggerService';

// Error types for better categorization
export enum ErrorType {
  AUTH = 'auth',
  NETWORK = 'network',
  VALIDATION = 'validation',
  SERVER = 'server',
  UNKNOWN = 'unknown'
}

// Standard error structure
export interface AppError {
  message: string;
  type: ErrorType;
  code?: string | number;
  originalError?: any;
  // Fields to support analytics
  timestamp?: string;
  path?: string;
  userContext?: string;
  original?: Error;
}

// Create consistent error objects
export const createError = (
  message: string,
  type: ErrorType = ErrorType.UNKNOWN,
  originalError?: any,
  code?: string | number
): AppError => {
  return {
    message,
    type,
    code,
    originalError,
    timestamp: new Date().toISOString(),
    path: window.location.pathname
  };
};

// Error logging that can be extended for analytics
export const logError = (error: AppError): void => {
  const errorContext = {
    type: error.type,
    code: error.code,
    path: error.path,
    timestamp: error.timestamp,
    userContext: error.userContext
  };

  // Log using our secure logger service
  logger.error(error.message, error.original || error.originalError, errorContext);
  
  // Add analytics tracking for errors in production
  if (config.isProduction && window.gtag) {
    try {
      window.gtag('event', 'error', {
        error_type: error.type,
        error_code: error.code || 'UNKNOWN',
        error_path: error.path
      });
    } catch (e) {
      // Silently fail analytics
    }
  }
};

// Handle API errors consistently
export const handleApiError = (error: any, defaultMessage: string = 'An error occurred'): AppError => {
  let errorType = ErrorType.UNKNOWN;
  let errorMessage = defaultMessage;
  let errorCode;
  
  // *** Check if this is the specific handled voting error BEFORE logging ***
  const isHandledVoteError = 
    (typeof error?.message === 'string' && error.message.toLowerCase().includes('already voted')) ||
    (typeof error?.cause?.message === 'string' && error.cause.message.toLowerCase().includes('already voted'));
  
  // Handle Supabase/auth errors
  if (error?.code?.startsWith('auth')) {
    errorType = ErrorType.AUTH;
    errorCode = error.code;
    
    // Map specific auth errors to user-friendly messages
    if (error.code === 'auth/invalid-login-credentials') {
      errorMessage = 'Invalid login credentials';
    } else if (error.code === 'auth/user-not-found') {
      errorMessage = 'Account not found';
    } else {
      errorMessage = 'Authentication error';
    }
  }
  // Handle network errors
  else if (error?.name === 'AbortError' || error?.name === 'TypeError' && error?.message.includes('network')) {
    errorType = ErrorType.NETWORK;
    errorMessage = 'Network connection error. Please check your internet connection.';
  }
  // Handle validation errors (usually 400 responses)
  else if (error?.status === 400 || error?.code === 400) {
    errorType = ErrorType.VALIDATION;
    errorMessage = error.message || 'Invalid request data';
    errorCode = 400;
  }
  // Handle server errors (usually 500 responses)
  else if (error?.status >= 500 || error?.code >= 500) {
    errorType = ErrorType.SERVER;
    errorMessage = 'Server error. Please try again later.';
    errorCode = error.status || error.code;
  }
  // Use provided message or get it from the error
  else if (error?.message) {
    errorMessage = error.message;
    errorCode = error.code || error.status;
  }
  
  const appError = createError(errorMessage, errorType, error, errorCode);
  
  // Log the error ONLY if it's not the handled vote error
  if (!isHandledVoteError) {
    logError(appError);
  }
  
  return appError;
};

// Get user-friendly error message
export const getUserFriendlyErrorMessage = (error: AppError): string => {
  switch (error.type) {
    case ErrorType.AUTH:
      return error.message || 'Authentication error. Please try again.';
    case ErrorType.NETWORK:
      return 'Network connection issue. Please check your internet connection.';
    case ErrorType.VALIDATION:
      return error.message || 'Please check your input and try again.';
    case ErrorType.SERVER:
      return 'Server error. Our team has been notified.';
    default:
      return error.message || 'An unexpected error occurred. Please try again.';
  }
};

export default {
  createError,
  handleApiError,
  logError,
  getUserFriendlyErrorMessage,
  ErrorType
}; 